import type { CapabilityCreateInputSchema } from "../../generated/core-api";

export const MARKDOWN_RETRIEVE_DOC_ID_PROPERTY = "doc_id";

export const DEFAULT_MARKDOWN_RETRIEVE_DOC_ID_DESCRIPTION =
  "Exact filename of the document to retrieve, as it appears in the source document it was extracted from.";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Structural check aligned with Pacific.Agentic.Api's
 * ``_validate_markdown_retrieve_input_schema``.
 *
 * Every clause the binding enforces is repeated here, because a schema this
 * says is valid is one the UI stops warning about: a card that looks healthy
 * while the binding refuses to resolve is worse than no check at all.
 *
 * Allowed: a custom ``doc_id`` description, and sibling keywords the binding
 * ignores such as ``additionalProperties``. Rejected: a root that is not
 * ``type: "object"``, a ``required`` list that is anything but exactly
 * ``["doc_id"]``, any property besides ``doc_id``, or a non-string ``doc_id``.
 */
export function isValidMarkdownRetrieveInputSchema(schema: unknown): boolean {
  if (!isRecord(schema) || schema.type !== "object") {
    return false;
  }
  const { required } = schema;
  if (
    !Array.isArray(required) ||
    required.length !== 1 ||
    required[0] !== MARKDOWN_RETRIEVE_DOC_ID_PROPERTY
  ) {
    return false;
  }
  if (!isRecord(schema.properties)) {
    return false;
  }
  const propertyNames = Object.keys(schema.properties);
  if (
    propertyNames.length !== 1 ||
    propertyNames[0] !== MARKDOWN_RETRIEVE_DOC_ID_PROPERTY
  ) {
    return false;
  }
  const docId = schema.properties[MARKDOWN_RETRIEVE_DOC_ID_PROPERTY];
  return isRecord(docId) && docId.type === "string";
}

export function parseMarkdownRetrieveDocIdDescription(
  schema: unknown,
): string {
  if (!isRecord(schema) || !isRecord(schema.properties)) {
    return DEFAULT_MARKDOWN_RETRIEVE_DOC_ID_DESCRIPTION;
  }
  const docId = schema.properties[MARKDOWN_RETRIEVE_DOC_ID_PROPERTY];
  if (!isRecord(docId) || typeof docId.description !== "string") {
    return DEFAULT_MARKDOWN_RETRIEVE_DOC_ID_DESCRIPTION;
  }
  return docId.description.trim() || DEFAULT_MARKDOWN_RETRIEVE_DOC_ID_DESCRIPTION;
}

export function buildMarkdownRetrieveInputSchema(
  docIdDescription: string,
): CapabilityCreateInputSchema {
  return {
    type: "object",
    properties: {
      [MARKDOWN_RETRIEVE_DOC_ID_PROPERTY]: {
        type: "string",
        description:
          docIdDescription.trim() ||
          DEFAULT_MARKDOWN_RETRIEVE_DOC_ID_DESCRIPTION,
      },
    },
    required: [MARKDOWN_RETRIEVE_DOC_ID_PROPERTY],
    additionalProperties: false,
  };
}

export const MARKDOWN_RETRIEVE_INPUT_SCHEMA = buildMarkdownRetrieveInputSchema(
  DEFAULT_MARKDOWN_RETRIEVE_DOC_ID_DESCRIPTION,
);
